import { faInfoCircle, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Col, Form, FormControl, Modal, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import TooltipButton from "../TooltipButton/TooltipButton";
import AxiosService from "../../utils/AxiosService";
import { dataDisplayRequest } from "../../utils/auth_service/AuthServiceHelper";
import CustomeAutoComplete from "../CustomeAutoComplete/CustomeAutoComplete";
import styles from "./CreateManuscriptModal.module.scss";
import history from "../../history";
import { useLocation } from "react-router-dom";

const CreateManuscriptModal = ({ show, onHide, onAutoOpen }) => {
	const apiService = new AxiosService();
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	
	// URL Query Parameter Management using existing history
	const updateURLParams = (params) => {
		const urlParams = new URLSearchParams(location.search);
		
		// Update or add parameters
		Object.keys(params).forEach(key => {
			if (params[key]) {
				urlParams.set(key, params[key]);
			} else {
				urlParams.delete(key);
			}
		});
		
		// Update URL without page reload using existing history
		const newSearch = urlParams.toString();
		const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
		history.replace(newPath);
	};
	
	const clearURLParams = () => {
		const urlParams = new URLSearchParams(location.search);
		urlParams.delete('modal');
		urlParams.delete('journalName');
		urlParams.delete('journalId');
		
		const newSearch = urlParams.toString();
		const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
		history.replace(newPath);
	};
	
	const getURLParams = () => {
		const urlParams = new URLSearchParams(location.search);
		return {
			modal: urlParams.get('modal'),
			journalName: urlParams.get('journalName'),
			journalId: urlParams.get('journalId')
		};
	};
	
	// Journal autocomplete state - following JournalSearch pattern
	const [journalListOption, setJournalListOption] = useState([]);
	const [journalLoading, setJournalLoading] = useState(false);
	const [selectedJournal, setSelectedJournal] = useState({ 
		journalTitle: "", 
		journalId: "" 
	});
	
	// New state for enhanced journal selection flow
	const [isJournalSelected, setIsJournalSelected] = useState(false);
	const [isJournalFieldDisabled, setIsJournalFieldDisabled] = useState(false);
	const [journalSearchLoading, setJournalSearchLoading] = useState(false);
	
	// Configurable Journal ID - change here for future updates
	const JOURNAL_ID = "RSC123";
	
	// Static values for form fields
	const STATIC_VALUES = {
		manuscriptType: "Research article",
		referenceType: "VANCOUVER", 
		language: "en_US",
		studyType: "Not applicable"
	};
	
	// Form state - includes journal title for autocomplete
	const [formData, setFormData] = useState({
		batchName: "",
		referenceCheck: "yes",
		journalTitle: ""
	});
	
	// Reference validation state
	const [referenceCitationFormat, setReferenceCitationFormat] = useState("squareBrackets");
	const [referenceCitationFormatError, setReferenceCitationFormatError] = useState("");
	const [journalConfig, setJournalConfig] = useState({
		referenceCitationsFormat: ["superScripts", "squareBrackets"]  // Default fallback
	});

	const getJournalConfig = (journalId = null) => {
		// Only call API if we have a journal ID
		if (!journalId) {
			// Use fallback config when no journal is selected
			setJournalConfig({
				referenceCitationsFormat: ["superScripts", "squareBrackets"]  // Default fallback
			});
			return;
		}

		// Try to get journal configuration for the selected journal
		apiService.getJournalGuidelines(journalId, null).then(res => {
			if (res.journalConfig) {
				setJournalConfig(res.journalConfig);
				// Set default reference citation format if only one option
				if (res.journalConfig.referenceCitationsFormat?.length === 1) {
					setReferenceCitationFormat(res.journalConfig.referenceCitationsFormat[0]);
				}
			} else {
				// API succeeded but no journal config - use fallback
				setJournalConfig({
					referenceCitationsFormat: ["superScripts", "squareBrackets"]
				});
			}
		}).catch(err => {
			// API call failed - use fallback config
			console.log("Using fallback journal config for journal:", journalId);
			setJournalConfig({
				referenceCitationsFormat: ["superScripts", "squareBrackets"]  // Default options
			});
		})
	}

	// Journal name search function - using autoSuggest API for autocomplete
	const getJournalName = (searchValue) => {
		setJournalLoading(true);
		
		// Use autoSuggest API for autocomplete suggestions
		const body = {
			"field": "journalTitle",
			"value": searchValue
		}
		
		apiService.getJournalNameOptions(body).then(res => {
			setJournalLoading(false);
			// Store simple journal names for autocomplete
			setJournalListOption(res.result || []);
		}).catch(err => {
			setJournalLoading(false);
			toast.error(err.response?.data?.message || "Failed to fetch journal suggestions", { 
				autoClose: 3000 
			});
			setJournalListOption([]);
		});
	}

	// Function to call journal/search API when journal name is selected
	const searchJournalByName = (journalName) => {
		setJournalSearchLoading(true);
		
		// Build config for journal/search API
		const config = {
			method: "post",
			url: "/api/v1/serch-service/search/journal?from=0&offset=10&operator=and",
			data: { 
				journal_title: journalName,
				impact_factor: [0, 8], // Default range
				'sort': 'desc',
				'field': 'Rank'
			}
		}
		
		// Use dataDisplayRequest to get journal object with ID
		dataDisplayRequest(config).then(response => {
			setJournalSearchLoading(false);
			
			if (response.data && response.data.length > 0) {
				// Find exact match by journal title
				const exactMatch = response.data.find(journal => 
					journal.journal_title.toLowerCase() === journalName.toLowerCase()
				);
				
				if (exactMatch) {
					// Set selected journal with ID
					setSelectedJournal({ 
						journalTitle: exactMatch.journal_title, 
						journalId: exactMatch.lexicon_id 
					});
					setIsJournalSelected(true);
					setIsJournalFieldDisabled(true);
					
					// Add journal info to URL
					updateURLParams({ 
						journalName: exactMatch.journal_title,
						journalId: exactMatch.lexicon_id
					});
					
					// Call getJournalConfig with the selected journal ID
					getJournalConfig(exactMatch.lexicon_id);
				} else {
					toast.error("Journal not found. Please select from the suggestions.", { autoClose: 3000 });
				}
			} else {
				toast.error("Journal not found. Please select from the suggestions.", { autoClose: 3000 });
			}
		}, error => {
			setJournalSearchLoading(false);
			toast.error(error?.response?.data?.error || "Failed to find journal details", {
				autoClose: 3000,
			});
		});
	}

	// Journal selection handlers - updated for new flow
	const handleJournalSelect = (selectedJournalName) => {
		// selectedJournalName is now a simple string from autoSuggest
		setFormData({ ...formData, journalTitle: selectedJournalName });
		
		// Call journal/search API to get the journal object with ID
		searchJournalByName(selectedJournalName);
	}

	const handleJournalSearchFormChange = (event) => {
		event.preventDefault();
		const { name, value } = event.target;
		setFormData({ ...formData, [name]: value });
	}

	// Clear journal selection functionality
	const handleClearJournal = () => {
		setFormData({ ...formData, journalTitle: "" });
		setSelectedJournal({ journalTitle: "", journalId: "" });
		setIsJournalSelected(false);
		setIsJournalFieldDisabled(false);
		setJournalListOption([]);
		setJournalConfig({
			referenceCitationsFormat: ["superScripts", "squareBrackets"]  // Reset to default
		});
		setReferenceCitationFormat("squareBrackets");
		setReferenceCitationFormatError("");
		
		// Clear journal info from URL
		updateURLParams({ 
			journalName: null,
			journalId: null
		});
	}

	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	}

	const validateForm = () => {
		const { batchName, referenceCheck, journalTitle } = formData;
		
		// Step 1: Journal Selection Validation - check if user selected from autocomplete
		if (!isJournalSelected || !selectedJournal.journalId) {
			toast.error("Please select a journal from the suggestions to continue", { autoClose: 3000 });
			return false;
		}
		
		// Step 2: Batch Name Validation (moved to second)
		if (!batchName.trim()) {
			toast.error("Please enter a name for your batch", { autoClose: 3000 });
			return false;
		}
		
		// Step 3: Reference Citation Format Validation - COMMENTED OUT
		// if (referenceCheck === "yes" && journalConfig?.referenceCitationsFormat?.length > 1 && !referenceCitationFormat) {
		// 	setReferenceCitationFormatError("Please select a reference citation format");
		// 	toast.error("Please select a reference citation format", { autoClose: 3000 });
		// 	return false;
		// }
		
		return true;
	}

	const handleCreateManuscript = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);
		
		try {
			// Step 1: Create manuscript with selected journal ID or fallback to hardcoded ID
			const manuscriptData = {
				journalId: selectedJournal.journalId || JOURNAL_ID,
				force: true,
				batchName: formData.batchName.trim()
			};

			const response = await apiService.createManuscript(manuscriptData);
			
			if (!response?.manuscriptId) {
				throw new Error("Manuscript creation failed - no manuscript ID returned");
			}

			// Step 2: Save Step One details with static values
			const stepOneData = {
				manuscriptType: STATIC_VALUES.manuscriptType,
				studyType: STATIC_VALUES.studyType,
				referenceFormat: STATIC_VALUES.referenceType,
				language: STATIC_VALUES.language,
				referenceCheck: formData.referenceCheck,
				manuscriptTitle: formData.batchName.trim(), // Use batch name as manuscript title
				readPoliciesAndComply: true,
				anonymizedPeerReview: true,
				readingChecklist: true
			};

			// Add referenceCitationFormat if reference checking is enabled - COMMENTED OUT
			// if (formData.referenceCheck === "yes" && referenceCitationFormat) {
			// 	stepOneData.referenceCitationFormat = referenceCitationFormat;
			// }

			await apiService.updateStepOneDetails(response.manuscriptId, stepOneData);
			
			toast.success("Your manuscript batch has been created successfully.", { autoClose: 3000 });
			
			// Clear URL parameters after successful manuscript creation
			clearURLParams();
			
			// Close modal
			onHide();
			
			// Step 3: Navigate to SimplifiedManuscriptSubmission
			history.push(`/manuscript-submission/${response.manuscriptId}`);
			
		} catch (error) {
			console.error("Manuscript creation error:", error);
			toast.error(error?.response?.data?.message || error?.message || "Failed to create manuscript", { autoClose: 3000 });
		} finally {
			setLoading(false);
		}
	}

	const handleClose = () => {
		// Check if we have journal data in URL to preserve
		const urlParams = getURLParams();
		
		if (urlParams.journalName && urlParams.journalId) {
			// Preserve journal selection from URL, only reset batch name
			setFormData({
				batchName: "",
				referenceCheck: "yes",
				journalTitle: urlParams.journalName
			});
			setSelectedJournal({ 
				journalTitle: urlParams.journalName, 
				journalId: urlParams.journalId 
			});
			setIsJournalSelected(true);
			setIsJournalFieldDisabled(true);
			
			// Remove modal=open but keep journal data
			updateURLParams({ 
				modal: null,
				journalName: urlParams.journalName,
				journalId: urlParams.journalId
			});
		} else {
			// No journal in URL, reset everything
			setFormData({
				batchName: "",
				referenceCheck: "yes",
				journalTitle: ""
			});
			setSelectedJournal({ journalTitle: "", journalId: "" });
			setIsJournalSelected(false);
			setIsJournalFieldDisabled(false);
			
			// Remove modal=open and clear all modal-related params
			updateURLParams({ 
				modal: null,
				journalName: null,
				journalId: null
			});
		}
		
		setJournalListOption([]);
		setReferenceCitationFormat("");
		setReferenceCitationFormatError("");
		setJournalSearchLoading(false);
		
		onHide();
	}

	useEffect(() => {
		if (show) {
			// Add modal status to URL when modal opens
			updateURLParams({ modal: 'open' });
			
			// Check if we have journal data in URL to restore state
			const urlParams = getURLParams();
			if (urlParams.journalName && urlParams.journalId) {
				// Restore journal selection from URL
				setFormData(prev => ({ ...prev, journalTitle: urlParams.journalName }));
				setSelectedJournal({ 
					journalTitle: urlParams.journalName, 
					journalId: urlParams.journalId 
				});
				setIsJournalSelected(true);
				setIsJournalFieldDisabled(true);
				
				// Get journal config without calling search API
				getJournalConfig(urlParams.journalId);
			}
		}
	}, [show]);

	// Handle URL parameters on component mount (for page refresh scenarios)
	useEffect(() => {
		const urlParams = getURLParams();
		
		// If modal is in URL but not currently shown, trigger modal open
		if (urlParams.modal === 'open' && !show && onAutoOpen) {
			// Call parent component to open the modal
			onAutoOpen();
			console.log('Auto-opening modal based on URL parameters');
		}
		
		// If we have journal data in URL but modal is not shown, 
		// we can prepare the state for when modal opens
		if (urlParams.journalName && urlParams.journalId && !show) {
			// Pre-populate journal data for when modal opens
			setFormData(prev => ({ ...prev, journalTitle: urlParams.journalName }));
			setSelectedJournal({ 
				journalTitle: urlParams.journalName, 
				journalId: urlParams.journalId 
			});
			setIsJournalSelected(true);
			setIsJournalFieldDisabled(true);
		}
	}, []); // Run only on mount

	return (
		<Modal 
			show={show} 
			onHide={handleClose} 
			size="lg" 
			dialogClassName={styles.createManuscriptModal}
			centered
		>
			<Modal.Header className={styles.modalHeader}>
				<Modal.Title>
					<h4>Start Compliance Check</h4>
					<p className="mb-0 text-muted">Start the formatting process by selecting a journal and entering your batch name.</p>
				</Modal.Title>
				<button onClick={handleClose} className={styles.closeButton}>
					<FontAwesomeIcon icon={faXmark} />
				</button>
			</Modal.Header>
			
			<Modal.Body className={styles.modalBody}>
				<Form>
					<Row>
						<Col md={12} className="mb-3">
							<label>
								Journal Name <span className="text-danger">*</span>
								<TooltipButton 
									tooltipMessage="Select the journal where you want to submit your manuscript. Start typing to see suggestions." 
									placement="top"
								>
									<FontAwesomeIcon icon={faInfoCircle} className={classNames(styles.labelButtonLink, "mx-2 align-middle")} />
								</TooltipButton>
							</label>
							<div className="d-flex align-items-center">
								<div className="flex-grow-1 mr-2">
									<CustomeAutoComplete
										name={'journalTitle'}
										placeholder='Enter journal name or ISSN/EISSN'
										searchValue={formData.journalTitle}
										setSearchValue={handleJournalSearchFormChange}
										handleSelect={handleJournalSelect}
										optionList={journalListOption}
										getOptions={getJournalName}
										getEnterSearch={() => {}} // No action needed on enter for this field
										loading={journalLoading}
										component="journal" // Handle journal objects
										disabled={isJournalFieldDisabled}
									/>
								</div>
								<TooltipButton 
									tooltipMessage="Reset Journal" 
									placement="top"
								>
									<button 
										type="button"
										className="btn btn-outline-secondary ms-2"
										onClick={handleClearJournal}
										disabled={!isJournalSelected || journalSearchLoading}
									>
										{journalSearchLoading ? (
											<Spinner animation="border" size="sm" />
										) : (
											<FontAwesomeIcon icon={faXmark} />
										)}
									</button>
								</TooltipButton>
							</div>
						</Col>

						<Col md={12} className="mb-3">
							<label>
								Batch Name <span className="text-danger">*</span>
								<TooltipButton 
									tooltipMessage="Please enter your batch name. This will be used to identify your manuscript." 
									placement="top"
								>
									<FontAwesomeIcon icon={faInfoCircle} className={classNames(styles.labelButtonLink, "mx-2 align-middle")} />
								</TooltipButton>
							</label>
							<FormControl
								className="mt-2"
								as="textarea"
								value={formData.batchName}
								rows={3}
								placeholder="Example: Neuro_Study_July2025"
								onChange={(e) => handleInputChange('batchName', e.target.value)}
								disabled={!isJournalSelected} // Disable until journal is selected
							/>
						</Col>

						{/* Reference validation functionality - COMMENTED OUT
						<Col md={12} className="mb-3">
							<div className="d-flex flex-column justify-content-between position-relative">
								{!isJournalSelected && (
									<div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" 
										 style={{ 
											backgroundColor: 'rgba(255, 255, 255, 0.8)', 
											zIndex: 10,
											borderRadius: '0.375rem'
										 }}>
										<div className="text-center p-3">
											<FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="lg" />
											<p className="mb-0 text-muted fw-bold">Please select a journal first</p>
											<small className="text-muted">Journal selection is required to configure reference validation options</small>
										</div>
									</div>
								)}
								
								<fieldset className="border rounded p-3" style={{ opacity: isJournalSelected ? 1 : 0.5 }}>
									<legend className="w-auto px-2 mb-2 h6">Validate my references</legend>
									<div className="d-flex mb-2">
										<input 
											aria-label="Validate reference radio button" 
											className="mr-2" 
											type="radio" 
											name="referenceCheck" 
											value="yes" 
											onChange={(e) => handleInputChange('referenceCheck', e.target.value)} 
											checked={formData.referenceCheck === "yes"}
											disabled={!isJournalSelected}
										/>
										I would like my references checked and confirm they are in the correct format
									</div>
									{formData.referenceCheck === "yes" && journalConfig?.referenceCitationsFormat?.length > 1 &&
										<div className="ml-4 mb-2">
											<div className="d-flex flex-column">
                                                <div className="d-flex align-items-center flex-row">
                                                    <label className="mb-0 text-nowrap">Reference citation format</label>
                                                    <span className="text-danger ml-1">*</span>
                                                </div>
												<select 
													className="form-control w-auto" 
													value={referenceCitationFormat}
													onChange={(e) => {
														setReferenceCitationFormat(e.target.value);
														setReferenceCitationFormatError("");
													}}
													disabled={!isJournalSelected}
												>
													<option value="">Select format</option>
													{journalConfig.referenceCitationsFormat?.includes("superScripts") && 
														<option value="superScripts">Superscript</option>
													}
													{journalConfig.referenceCitationsFormat?.includes("squareBrackets") && 
														<option value="squareBrackets">Square brackets (please ensure no other text is within square brackets)</option>
													}
													{!journalConfig.referenceCitationsFormat?.includes("superScripts") && !journalConfig.referenceCitationsFormat?.includes("squareBrackets") &&
														<>
															<option value="superScripts">Superscript</option>
															<option value="squareBrackets">Square brackets (please ensure no other text is within square brackets)</option>
														</>
													}
												</select>
											</div>
											<span className="text-danger">{referenceCitationFormatError}</span>
										</div>
									}
									<div className="d-flex">
											<input 
												aria-label="Don't validate reference radio button" 
												className="mr-2" 
												type="radio" 
												name="referenceCheck" 
												value="no" 
												onChange={(e) => handleInputChange('referenceCheck', e.target.value)} 
												checked={formData.referenceCheck === "no"}
												disabled={!isJournalSelected}
											/>
										Don't check my references
									</div>
								</fieldset>
							</div>
						</Col> */}
					</Row>
				</Form>
			</Modal.Body>

			<Modal.Footer className={styles.modalFooter}>
				<button 
					className="btn btn-outline-secondary" 
					onClick={handleClose}
					disabled={loading}
				>
					Cancel
				</button>
				<button 
					className="px-4" 
					onClick={handleCreateManuscript}
					disabled={loading}
				>
					{loading ? (
						<>
							<Spinner animation="border" size="sm" className="mr-2" />
							Creating...
						</>
					) : (
						"Proceed to Upload"
					)}
				</button>
			</Modal.Footer>
		</Modal>
	);
}

export default CreateManuscriptModal; 